import * as R from '@radix-ui/react-dialog';
import {memo, type ReactNode} from 'react';
import {clsx} from 'clsx';
import {Button} from '../button';
import styles from './dialog.module.css';

type Props = {
  /** default: 'primary' */
  variant?: 'primary' | 'danger';
  /** タイトル */
  title?: string;
  /** プライマリボタンのテキスト, default: 'OK' */
  agreeBtnText?: string;
  /** キャンセルボタンのテキスト, default: 'キャンセル' */
  cancelBtnText?: string;
  /** プライマリボタンのクリックハンドラ, 非同期処理の場合 await されずにダイアログが閉じる */
  onAgree?: () => void;
  /** キャンセルボタンのクリックハンドラ, 非同期処理の場合 await されずにダイアログが閉じる */
  onCancel?: () => void;
  /** キャンセルボタンを表示しない */
  hideCancelButton?: boolean;
  /** モーダルを閉じる関数 */
  close: () => void;
  /** ダイアログのメインコンテンツ */
  children?: ReactNode;
};

/**
 * 必ず useDialog を通して使用し、 直接使用しない
 * @see https://www.notion.so/13acf8c9a0374b68a83b91244db0e962
 */
export const Dialog = memo<Props>(
  ({
    title,
    variant = 'primary',
    agreeBtnText: agreeButtonText = 'OK',
    cancelBtnText: cancelButtonText = 'キャンセル',
    onAgree,
    onCancel,
    hideCancelButton = false,
    close,
    children,
  }) => {
    return (
      <R.Root
        open
        onOpenChange={(opened) => {
          if (!opened) {
            close();
          }
        }}
      >
        <R.Portal>
          <R.Overlay className={styles.overlay} />
          <div className={styles.contentWrapper}>
            <R.Content
              className={styles.content}
              onEscapeKeyDown={(event) => {
                event.preventDefault();
              }}
              onInteractOutside={(event) => {
                event.preventDefault();
              }}
            >
              {title !== undefined && (
                <div className={styles.header}>
                  <span className={styles.title}>{title}</span>
                </div>
              )}
              <div className={styles.body}>{children}</div>
              <div
                className={clsx(
                  styles.footer,
                  variant === 'danger'
                    ? styles.footerDanger
                    : styles.footerPrimary,
                )}
              >
                <R.Close asChild>
                  <Button
                    variant='flat'
                    color={variant}
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: '700',
                    }}
                    data-testid='test-id'
                    onClick={onAgree}
                  >
                    {agreeButtonText}
                  </Button>
                </R.Close>
                {!hideCancelButton && (
                  <R.Close asChild>
                    <Button
                      variant='flat'
                      color='secondary'
                      style={{fontSize: 'var(--font-size-sm)'}}
                      onClick={onCancel}
                    >
                      {cancelButtonText}
                    </Button>
                  </R.Close>
                )}
              </div>
            </R.Content>
          </div>
        </R.Portal>
      </R.Root>
    );
  },
);

Dialog.displayName = 'Dialog';
